
import Vue from 'vue';
import RuleService from '@/services/RuleService';
import TagService from '@/services/TagService';
import UserService from '@/services/UserService';
import { extractErrorMessage } from '@/utils/Util';
import RuleActionDialog from '@/components/entities/rule/RuleActionDialog.vue';
import RuleScopeFiltersCard from '@/components/entities/rule/RuleScopeFiltersCard.vue';
import { formatRuleActionLines, FormattedLine } from './ruleActionFormatter';
import {
  scopeEntityTypeOptions,
  scopeTriggerOptions,
  normalizeScopeFilters,
  hasInvalidRegexPair,
} from './ruleConstants';

export default Vue.extend({
  name: 'RuleEditDialog',

  components: {
    RuleActionDialog,
    RuleScopeFiltersCard,
  },

  props: {
    rule: { type: Object, required: true },
  },

  data(viewModel: any): any {
    return {
      tags: [] as any[],
      users: [] as any[],
      localDialog: false,
      dismissTimeout: null as ReturnType<typeof setTimeout> | null,
      localRule: {} as any,

      isValid: true,
      isLoading: false,

      alertText: '',
      isAlertVisible: false,
      alertType: 'error',

      applyToExisting: false,

      requiredFieldRules: [
        (fieldValue: string) => !!fieldValue || viewModel.$t('required'),
      ],
      requiredEntityTypeRules: [
        (entityTypeValue: string) =>
          !!entityTypeValue || viewModel.$t('required'),
      ],
      requiredTriggersRules: [
        (selectedTriggers: string[]) =>
          (!!selectedTriggers && selectedTriggers.length > 0) ||
          viewModel.$t('rule.atLeastOneTrigger'),
      ],

      scopeEntityTypeOptions,
      scopeTriggerOptions,
    };
  },

  methods: {
    close() {
      this.localDialog = false;
    },

    clear() {
      this.isAlertVisible = false;
      this.isLoading = false;
      this.applyToExisting = false;
    },

    dismiss() {
      this.clear();
      this.close();
    },

    validate() {
      if (!this.editForm) return;

      const isFormValid = this.editForm.validate();
      if (!isFormValid) return;

      if (hasInvalidRegexPair(this.localRule.scopeFilters)) {
        this.alertType = 'error';
        this.alertText = this.$t('rule.regexPairError') as string;
        this.isAlertVisible = true;
        return;
      }

      this.save();
    },

    addAction(actionItem: any) {
      if (!this.localRule.actions) this.localRule.actions = [];
      this.localRule.actions.push(actionItem);
    },

    updateAction(actionIndex: number, actionItem: any) {
      this.localRule.actions.splice(actionIndex, 1, actionItem);
    },

    removeAction(actionIndex: number) {
      this.localRule.actions.splice(actionIndex, 1);
    },

    formatActionLines(actionItem: any): FormattedLine[] {
      return formatRuleActionLines(actionItem, {
        resolveTag: (tagUuid: string) => {
          const foundTag = this.tags.find((tag: any) => tag.uuid === tagUuid);
          return foundTag ? foundTag.name : tagUuid;
        },
        resolveUser: (userUuid: string) => {
          const foundUser = this.users.find(
            (user: any) => user.uuid === userUuid
          );
          return foundUser ? foundUser.email : userUuid;
        },
      });
    },

    getActionLineLabel(actionLineKey: string): string {
      if (actionLineKey === 'condition') {
        const translationKey = 'rule.condition.title';
        const translatedTitle = this.$t(translationKey) as string;
        return translatedTitle !== translationKey
          ? translatedTitle
          : 'Condition';
      }

      const actionTranslationKey = `rule.action.${actionLineKey}`;
      const translatedLabel = this.$t(actionTranslationKey) as string;
      if (translatedLabel !== actionTranslationKey) return translatedLabel;

      const simplifiedLineKey = actionLineKey.replace(/Id$/, '');
      const fallbackTranslationKey = `rule.action.${simplifiedLineKey}`;
      const fallbackTranslatedLabel = this.$t(fallbackTranslationKey) as string;
      if (fallbackTranslatedLabel !== fallbackTranslationKey)
        return fallbackTranslatedLabel;

      return simplifiedLineKey;
    },

    getActionTitle(actionType: string): string {
      const actionTitleMap: Record<string, string> = {
        'add-alert': this.$t('rule.action.addAlert') as string,
        'add-tag': this.$t('rule.action.addTag') as string,
        'add-permission': this.$t('rule.action.addPermission') as string,
      };

      return actionTitleMap[actionType] ?? actionType;
    },

    async reapply() {
      this.isAlertVisible = false;
      this.isLoading = true;

      try {
        await RuleService.updateRule(
          this.currentTenant,
          (this.rule as any).uuid,
          { applyToExisting: true }
        );

        this.alertType = 'success';
        this.alertText = this.$t('rule.successUpdate') as string;
        this.isAlertVisible = true;
      } catch (error: any) {
        this.alertType = 'error';
        this.alertText =
          (this.$t('rule.errorUpdate') as string) +
          ' ' +
          extractErrorMessage(error);
        this.isAlertVisible = true;
      } finally {
        this.isLoading = false;
      }
    },

    async save() {
      this.isAlertVisible = false;
      this.isLoading = true;

      const attributesToUpdate: any = {};

      if (this.localRule.name !== this.rule.name)
        attributesToUpdate.name = this.localRule.name;
      if (this.localRule.description !== this.rule.description)
        attributesToUpdate.description = this.localRule.description;
      if (this.localRule.scopeEntityType !== this.rule.scopeEntityType)
        attributesToUpdate.scopeEntityType = this.localRule.scopeEntityType;

      if (
        JSON.stringify(this.localRule.scopeTriggers || []) !==
        JSON.stringify(this.rule.scopeTriggers || [])
      ) {
        attributesToUpdate.scopeTriggers = this.localRule.scopeTriggers;
      }

      if (
        JSON.stringify(this.localRule.actions || []) !==
        JSON.stringify(this.rule.actions || [])
      ) {
        attributesToUpdate.actions = this.localRule.actions;
      }

      const normalizedLocalFilters = normalizeScopeFilters(
        this.localRule.scopeFilters
      );
      const normalizedOriginalFilters = normalizeScopeFilters(
        (this.rule as any).scopeFilters
      );

      if (
        JSON.stringify(normalizedLocalFilters) !==
        JSON.stringify(normalizedOriginalFilters)
      ) {
        attributesToUpdate.scopeFilters = normalizedLocalFilters;
      }

      if (this.applyToExisting) {
        attributesToUpdate.applyToExisting = true;
      }

      try {
        const updatedRule = await RuleService.updateRule(
          this.currentTenant,
          (this.rule as any).uuid,
          attributesToUpdate
        );

        this.alertType = 'success';
        this.alertText = this.$t('rule.successUpdate') as string;
        this.isAlertVisible = true;

        this.localRule = updatedRule;
        this.$emit('update', updatedRule);

        this.dismissTimeout = setTimeout(() => this.dismiss(), 1200);
      } catch (error: any) {
        this.alertType = 'error';
        this.alertText =
          (this.$t('rule.errorUpdate') as string) +
          ' ' +
          extractErrorMessage(error);
        this.isAlertVisible = true;
        this.isLoading = false;
      }
    },

    async loadOptions() {
      try {
        this.tags = await TagService.getAllCollectionPages(this.currentTenant);
        this.users = await UserService.getAllCollectionPages({
          tenant: this.currentTenant,
        });
      } catch (error) {
        console.error('Failed to load tags/users', error);
      }
    },
  },

  beforeDestroy() {
    if (this.dismissTimeout) clearTimeout(this.dismissTimeout);
  },

  watch: {
    rule: {
      immediate: true,
      handler() {
        this.localRule = JSON.parse(JSON.stringify(this.rule));
        if (!this.localRule.scopeFilters) this.localRule.scopeFilters = [{}];
      },
    },
    localDialog(isOpen: boolean) {
      if (isOpen) this.loadOptions();
      else this.clear();
    },
  },

  computed: {
    editForm(): any {
      return this.$refs.editForm;
    },
    currentTenant(): any {
      return this.$root.$store.state.session.selectedTenant.uuid;
    },
    isDirty(): boolean {
      const normalizeValue = (inputValue: any) => {
        try {
          return JSON.stringify(inputValue ?? null);
        } catch {
          return String(inputValue);
        }
      };

      const originalRuleData = {
        name: this.rule.name,
        description: this.rule.description,
        scopeEntityType: this.rule.scopeEntityType,
        scopeTriggers: this.rule.scopeTriggers || [],
        scopeFilters: normalizeScopeFilters(
          (this.rule as any).scopeFilters
        ),
        actions: this.rule.actions || [],
      };

      const currentRuleData = {
        name: this.localRule.name,
        description: this.localRule.description,
        scopeEntityType: this.localRule.scopeEntityType,
        scopeTriggers: this.localRule.scopeTriggers || [],
        scopeFilters: normalizeScopeFilters(this.localRule.scopeFilters),
        actions: this.localRule.actions || [],
      };

      return (
        normalizeValue(originalRuleData) !== normalizeValue(currentRuleData) ||
        this.applyToExisting === true
      );
    },
  },
});
